import moment from 'moment';
import { useEffect, useMemo, useRef, useState } from 'react';
import { FiCalendar, FiChevronLeft, FiChevronRight } from 'react-icons/fi';

import * as St from './styles';

const WEEK_DAY_KEYS = [
  { key: 'dom', label: 'D' },
  { key: 'seg', label: 'S' },
  { key: 'ter', label: 'T' },
  { key: 'qua', label: 'Q' },
  { key: 'qui', label: 'Q' },
  { key: 'sex', label: 'S' },
  { key: 'sab', label: 'S' },
];
const MONTHS = [
  'janeiro',
  'fevereiro',
  'março',
  'abril',
  'maio',
  'junho',
  'julho',
  'agosto',
  'setembro',
  'outubro',
  'novembro',
  'dezembro',
];

interface DateFieldProps {
  value: Date | null;
  onChange: (date: Date) => void;
  minDate?: Date;
  maxDate?: Date;
  disabled?: boolean;
}

const DateField = ({
  value,
  onChange,
  minDate,
  maxDate,
  disabled,
}: DateFieldProps): JSX.Element => {
  const [open, setOpen] = useState(false);
  const [viewMonth, setViewMonth] = useState(moment(value || undefined));
  const fieldRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (fieldRef.current && !fieldRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const days = useMemo(() => {
    const start = viewMonth.clone().startOf('month').startOf('week');
    return Array.from({ length: 42 }, (_, index) => start.clone().add(index, 'day'));
  }, [viewMonth]);

  const isDisabledDay = (day: moment.Moment) => {
    if (minDate && day.isBefore(minDate, 'day')) return true;
    if (maxDate && day.isAfter(maxDate, 'day')) return true;
    return false;
  };

  const selectDay = (day: moment.Moment) => {
    if (isDisabledDay(day)) return;

    onChange(day.toDate());
    setViewMonth(day.clone());
    setOpen(false);
  };

  return (
    <St.DateFieldWrap ref={fieldRef}>
      <St.DateFieldButton
        type="button"
        disabled={disabled}
        onClick={() => setOpen((prev) => !prev)}
      >
        <St.DateFieldValue $empty={!value}>
          {value ? moment(value).format('DD/MM/YYYY') : 'DD/MM/YYYY'}
        </St.DateFieldValue>
        <FiCalendar />
      </St.DateFieldButton>
      {open && !disabled && (
        <St.Calendar>
          <St.CalendarHeader>
            <button
              type="button"
              onClick={() =>
                setViewMonth((current) => current.clone().subtract(1, 'month'))
              }
            >
              <FiChevronLeft />
            </button>
            <strong>
              {MONTHS[viewMonth.month()]}
              {' '}
              {viewMonth.year()}
            </strong>
            <button
              type="button"
              onClick={() =>
                setViewMonth((current) => current.clone().add(1, 'month'))
              }
            >
              <FiChevronRight />
            </button>
          </St.CalendarHeader>
          <St.CalendarWeek>
            {WEEK_DAY_KEYS.map(({ key, label }) => (
              <span key={key}>{label}</span>
            ))}
          </St.CalendarWeek>
          <St.CalendarGrid>
            {days.map((day) => {
              const isSelected = value && day.isSame(value, 'day');
              const isCurrentMonth = day.month() === viewMonth.month();
              const isDisabled = isDisabledDay(day);

              return (
                <St.CalendarDay
                  key={day.format('YYYY-MM-DD')}
                  type="button"
                  $selected={isSelected}
                  $outside={!isCurrentMonth}
                  $disabled={isDisabled}
                  disabled={isDisabled}
                  onClick={() => selectDay(day)}
                >
                  {day.date()}
                </St.CalendarDay>
              );
            })}
          </St.CalendarGrid>
        </St.Calendar>
      )}
    </St.DateFieldWrap>
  );
};

export default DateField;
